/**
 * WatchModule -- Curated content discovery hub for free educational
 * videos, documentaries, podcasts, TV, and radio.
 *
 * Tabs: For You, Documentaries, Learn, Kids, Podcasts, Live, Islamic
 * Features: search, filters, watch later, favorites, parental controls,
 *           inline video embed, audio player for podcasts
 */

import type { AweniContext, AweniModule } from '../../types';
import {
  type ContentItem,
  type WatchTab,
  CONTENT_CATALOG,
  LANG_FLAGS,
  SOURCE_COLORS,
  CATEGORY_ICONS,
  getContentByTab,
  getFeaturedContent,
  searchContent,
  filterContent,
  isEmbeddable,
} from './ContentCatalog';
import { VideoEmbed } from './VideoEmbed';
import { AudioPlayer } from './AudioPlayer';
import { getParentalControlService } from '../../services/ParentalControlService';

// ---------- Storage Keys ----------

const LS_WATCH_LATER = 'aweni_watch_later';
const LS_FAVORITES = 'aweni_watch_favorites';
const LS_CONTINUE = 'aweni_watch_continue';
const LS_HIDDEN = 'aweni_watch_hidden';

// ---------- Tab Definitions ----------

interface TabDef {
  id: WatchTab;
  labelKey: string;
  icon: string;
}

const TABS: TabDef[] = [
  { id: 'for_you', labelKey: 'watch_for_you', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>` },
  { id: 'documentaries', labelKey: 'watch_documentaries', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>` },
  { id: 'learn', labelKey: 'watch_learn', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>` },
  { id: 'kids', labelKey: 'watch_kids', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>` },
  { id: 'podcasts', labelKey: 'watch_podcasts', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zm-18 0a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>` },
  { id: 'live', labelKey: 'watch_live', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>` },
  { id: 'islamic', labelKey: 'watch_islamic', icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>` },
];

// ---------- WatchModule Class ----------

export default class WatchModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  private activeTab: WatchTab = 'for_you';
  private searchQuery = '';
  private activeFilters: { language?: string; category?: string; ageRange?: string; duration?: 'short' | 'medium' | 'long' } = {};
  private showFilters = false;
  private videoEmbed: VideoEmbed;
  private audioPlayer: AudioPlayer;
  private viewingContent: ContentItem | null = null;
  private featuredIndex = 0;
  private featuredInterval: ReturnType<typeof setInterval> | null = null;
  private showingSaved: 'later' | 'favorites' | null = null;

  constructor() {
    this.videoEmbed = new VideoEmbed();
    this.audioPlayer = new AudioPlayer();
  }

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = ctx;

    // [DECISION] If active profile is a child, force Kids tab only
    const profile = ctx.auth.user;
    if (profile && (profile as unknown as Record<string, unknown>).plan === 'free') {
      // Check parental: handled in render logic
    }

    this._injectStyles();
    this.renderBrowse();
    this._startFeaturedRotation();
  }

  unmount(): void {
    this._stopFeaturedRotation();
    this.videoEmbed.destroy();
    this.audioPlayer.destroy();
    if (this.container) {
      this.container.innerHTML = '';
      this.container = null;
    }
    this.ctx = null;
    this.viewingContent = null;
  }

  onRouteChange(sub: string): void {
    if (!sub) {
      this.viewingContent = null;
      this.showingSaved = null;
      this.renderBrowse();
      return;
    }

    // [DECISION] Handle deep link: ?play=content_id from WatchWidget or hash navigation
    if (sub.startsWith('?play=')) {
      const contentId = sub.replace('?play=', '');
      const item = CONTENT_CATALOG.find((c) => c.id === contentId);
      if (item) {
        this._openContent(item);
      } else {
        this.renderBrowse();
      }
    }
  }

  // ---------- Main Browse View ----------

  private renderBrowse(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const locale = this.ctx.i18n.locale;

    // Get visible tabs (hide Islamic if memoriz module not relevant)
    const visibleTabs = this._getVisibleTabs();
    const tabContent = this._getTabContent(locale);

    this.container.innerHTML = '';

    const root = document.createElement('div');
    root.className = 'watch-module';
    root.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    root.style.cssText = 'padding-bottom:100px;';

    // Header with title and saved icon
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:8px 16px 0;';
    header.innerHTML = `
      <h1 class="text-xl font-bold" style="color:var(--text-primary);">${t('watch_title')}</h1>
      <button class="watch-saved-btn" aria-label="${t('watch_saved')}" style="width:36px;height:36px;border-radius:50%;border:none;background:var(--bg-secondary);color:var(--text-primary);cursor:pointer;display:flex;align-items:center;justify-content:center;">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
      </button>
    `;
    root.appendChild(header);

    // Search bar
    const searchWrap = document.createElement('div');
    searchWrap.style.cssText = 'padding:12px 16px 0;';
    searchWrap.innerHTML = `
      <div style="position:relative;">
        <input type="search" class="watch-search w-full px-4 py-3 rounded-xl text-sm outline-none" style="background:var(--card-bg);color:var(--text-primary);border:1px solid var(--border);padding-${isRtl ? 'right' : 'left'}:40px;" placeholder="${t('watch_search')}" value="${this._escapeHtml(this.searchQuery)}" aria-label="${t('watch_search')}"/>
        <svg style="position:absolute;${isRtl ? 'right' : 'left'}:12px;top:50%;transform:translateY(-50%);pointer-events:none;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <button class="watch-filter-btn" style="position:absolute;${isRtl ? 'left' : 'right'}:8px;top:50%;transform:translateY(-50%);width:32px;height:32px;border-radius:8px;border:none;background:${this.showFilters || Object.keys(this.activeFilters).length ? 'var(--accent)' : 'transparent'};color:${this.showFilters || Object.keys(this.activeFilters).length ? 'white' : 'var(--text-secondary)'};cursor:pointer;display:flex;align-items:center;justify-content:center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
        </button>
      </div>
    `;
    root.appendChild(searchWrap);

    // Filter panel (collapsible)
    if (this.showFilters) {
      const filterPanel = this._renderFilterPanel(t, locale);
      root.appendChild(filterPanel);
    }

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'watch-tabs';
    tabBar.style.cssText = 'display:flex;gap:6px;overflow-x:auto;padding:12px 16px 0;-webkit-overflow-scrolling:touch;scrollbar-width:none;';
    tabBar.setAttribute('role', 'tablist');

    visibleTabs.forEach((tab) => {
      const isActive = tab.id === this.activeTab;
      const btn = document.createElement('button');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', String(isActive));
      btn.dataset.tab = tab.id;
      btn.style.cssText = `
        display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:20px;border:none;
        cursor:pointer;font-size:13px;font-weight:500;white-space:nowrap;flex-shrink:0;
        transition:all 200ms;
        ${isActive
          ? 'background:var(--accent);color:var(--accent-text);box-shadow:0 2px 8px rgba(0,0,0,0.15);'
          : 'background:var(--card-bg);color:var(--text-secondary);border:1px solid var(--border);'}
      `;
      btn.innerHTML = `${tab.icon}<span>${t(tab.labelKey)}</span>`;
      tabBar.appendChild(btn);
    });
    root.appendChild(tabBar);

    // Content area
    const contentArea = document.createElement('div');
    contentArea.className = 'watch-content';
    contentArea.style.cssText = 'padding:12px 16px;';

    if (this.activeTab === 'for_you') {
      this._renderForYouTab(contentArea, tabContent, t, locale, isRtl);
    } else {
      this._renderContentGrid(contentArea, tabContent, t, locale, isRtl);
    }

    root.appendChild(contentArea);
    this.container.appendChild(root);

    // [DECISION] Intercept ALL anchor clicks to prevent leaving the app
    this._interceptExternalLinks(root);

    // Bind events
    this._bindBrowseEvents(root);
  }

  // ---------- For You Tab ----------

  private _renderForYouTab(
    container: HTMLElement,
    items: ContentItem[],
    t: (key: string) => string,
    locale: string,
    isRtl: boolean,
  ): void {
    // Featured rotating card
    const featured = getFeaturedContent();
    if (featured.length > 0) {
      const current = featured[this.featuredIndex % featured.length];
      const featuredEl = document.createElement('div');
      featuredEl.className = 'watch-featured';
      featuredEl.style.cssText = `
        position:relative;border-radius:20px;overflow:hidden;margin-bottom:20px;
        cursor:pointer;aspect-ratio:16/9;background:#000;
      `;
      featuredEl.dataset.contentId = current.id;
      featuredEl.innerHTML = `
        ${current.thumbnail
          ? `<img src="${current.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;opacity:0.7;" loading="lazy"/>`
          : `<div style="width:100%;height:100%;background:linear-gradient(135deg,var(--accent),#3B82F6);"></div>`
        }
        <div style="position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.8) 0%,transparent 60%);"></div>
        <div style="position:absolute;bottom:0;${isRtl ? 'right' : 'left'}:0;padding:20px;width:100%;">
          <span class="px-2 py-1 rounded-full text-xs font-bold" style="background:${SOURCE_COLORS[current.source] || 'var(--accent)'};color:white;">${current.source}</span>
          <h2 class="text-lg font-bold mt-2" style="color:white;">${this._escapeHtml(current.title[locale] || current.title.en)}</h2>
          <p class="text-xs mt-1 line-clamp-2" style="color:rgba(255,255,255,0.8);">${this._escapeHtml(current.description[locale] || current.description.en)}</p>
        </div>
        <div style="position:absolute;top:12px;${isRtl ? 'left' : 'right'}:12px;display:flex;gap:6px;">
          ${current.duration ? `<span class="px-2 py-1 rounded-full text-xs font-bold" style="background:rgba(0,0,0,0.6);color:white;">${current.duration} min</span>` : ''}
          ${featured.length > 1 ? `<span class="text-xs" style="color:rgba(255,255,255,0.6);">${(this.featuredIndex % featured.length) + 1}/${featured.length}</span>` : ''}
        </div>
        <button class="watch-featured-play" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:56px;height:56px;border-radius:50%;background:rgba(255,255,255,0.9);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(0,0,0,0.3);">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="var(--accent)"><polygon points="9,5 19,12 9,19"/></svg>
        </button>
      `;
      container.appendChild(featuredEl);
    }

    // Continue watching section
    const continueList = this._getContinueWatching();
    if (continueList.length > 0) {
      const section = this._renderSection(t('watch_continue'), continueList, locale, isRtl);
      container.appendChild(section);
    }

    // Trending in language
    const trendingItems = items
      .filter((i) => i.languages.includes(locale))
      .slice(0, 8);
    if (trendingItems.length > 0) {
      const langName = locale === 'en' ? 'English' : locale === 'fr' ? 'French' : locale === 'ar' ? 'Arabic' : locale === 'es' ? 'Spanish' : 'German';
      const section = this._renderSection(
        `${t('watch_trending')} ${LANG_FLAGS[locale] || ''} ${langName}`,
        trendingItems,
        locale,
        isRtl,
      );
      container.appendChild(section);
    }

    // Recommended based on modules
    const mathItems = CONTENT_CATALOG.filter((i) => i.category === 'math' && i.family_safe).slice(0, 4);
    if (mathItems.length > 0) {
      const section = this._renderSection(t('watch_recommended'), mathItems, locale, isRtl);
      container.appendChild(section);
    }

    // All for-you items
    if (items.length > 0) {
      const allTitle = document.createElement('h3');
      allTitle.className = 'text-sm font-bold uppercase tracking-wide mb-3 mt-4';
      allTitle.style.cssText = 'color:var(--text-secondary);';
      allTitle.textContent = t('watch_all_content');
      container.appendChild(allTitle);
      this._renderCardList(container, items, locale, isRtl);
    }
  }

  // ---------- Content Grid (other tabs) ----------

  private _renderContentGrid(
    container: HTMLElement,
    items: ContentItem[],
    t: (key: string) => string,
    locale: string,
    isRtl: boolean,
  ): void {
    if (items.length === 0) {
      container.innerHTML = `<p class="text-sm text-center py-12" style="color:var(--text-secondary);">${t('watch_no_results')}</p>`;
      return;
    }

    this._renderCardList(container, items, locale, isRtl);
  }

  // ---------- Section (horizontal scroll) ----------

  private _renderSection(title: string, items: ContentItem[], locale: string, isRtl: boolean): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom:20px;';

    const heading = document.createElement('h3');
    heading.className = 'text-sm font-bold uppercase tracking-wide mb-3';
    heading.style.cssText = 'color:var(--text-secondary);';
    heading.textContent = title;
    section.appendChild(heading);

    const scroll = document.createElement('div');
    scroll.style.cssText = 'display:flex;gap:12px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;padding-bottom:4px;';

    items.forEach((item) => {
      const card = this._renderCompactCard(item, locale, isRtl);
      card.style.cssText += 'min-width:200px;max-width:200px;flex-shrink:0;';
      scroll.appendChild(card);
    });

    section.appendChild(scroll);
    return section;
  }

  // ---------- Card Rendering ----------

  private _renderCardList(container: HTMLElement, items: ContentItem[], locale: string, isRtl: boolean): void {
    const grid = document.createElement('div');
    grid.style.cssText = 'display:flex;flex-direction:column;gap:12px;';

    items.forEach((item) => {
      const card = this._renderFullCard(item, locale, isRtl);
      grid.appendChild(card);
    });

    container.appendChild(grid);
  }

  private _renderFullCard(item: ContentItem, locale: string, isRtl: boolean): HTMLElement {
    const card = document.createElement('div');
    card.className = 'watch-card';
    card.dataset.contentId = item.id;
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', item.title[locale] || item.title.en);
    card.style.cssText = `
      background:var(--card-bg);border-radius:16px;overflow:hidden;
      box-shadow:var(--shadow-sm);cursor:pointer;transition:transform 150ms;
    `;

    const title = item.title[locale] || item.title.en;
    const desc = item.description[locale] || item.description.en;
    const sourceColor = SOURCE_COLORS[item.source] || 'var(--accent)';
    const isLive = item.type === 'live_tv' || item.type === 'live_radio';
    const catIcon = CATEGORY_ICONS[item.category] || '';
    const langFlags = item.languages.map((l) => LANG_FLAGS[l] || l).join(' ');

    card.innerHTML = `
      <div style="display:flex;gap:12px;padding:12px;">
        <!-- Thumbnail -->
        <div style="position:relative;width:120px;height:80px;border-radius:12px;overflow:hidden;flex-shrink:0;background:var(--bg-secondary);">
          ${item.thumbnail
            ? `<img src="${item.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;" loading="lazy"/>`
            : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${sourceColor}40,${sourceColor}20);display:flex;align-items:center;justify-content:center;">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${sourceColor}" stroke-width="2"><polygon points="5,3 19,12 5,21"/></svg>
              </div>`
          }
          ${item.duration ? `<span style="position:absolute;bottom:4px;${isRtl ? 'left' : 'right'}:4px;background:rgba(0,0,0,0.75);color:white;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:600;">${item.duration}m</span>` : ''}
          ${isLive ? `<span style="position:absolute;top:4px;${isRtl ? 'right' : 'left'}:4px;background:#EF4444;color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;animation:livePulse 2s ease-in-out infinite;">LIVE</span>` : ''}
        </div>

        <!-- Info -->
        <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;">
          <div>
            <p class="text-sm font-bold line-clamp-2" style="color:var(--text-primary);margin-bottom:2px;">${this._escapeHtml(title)}</p>
            <p class="text-xs line-clamp-1" style="color:var(--text-secondary);">${this._escapeHtml(desc)}</p>
          </div>
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:4px;">
            <span class="px-2 py-0.5 rounded-full text-xs font-medium" style="background:${sourceColor};color:white;font-size:10px;">${item.source}</span>
            ${catIcon ? `<span style="display:flex;align-items:center;gap:2px;color:var(--text-secondary);">${catIcon}<span class="text-xs capitalize">${item.category}</span></span>` : ''}
            <span class="text-xs" style="color:var(--text-secondary);">${langFlags}</span>
            ${item.age_range === 'kids' ? `<span class="px-1.5 py-0.5 rounded text-xs" style="background:#FEF3C7;color:#92400E;font-size:9px;">Kids</span>` : ''}
          </div>
        </div>

        <!-- 3-dot menu -->
        <button class="watch-card-menu" data-id="${item.id}" aria-label="Menu" style="width:28px;height:28px;border-radius:8px;border:none;background:transparent;color:var(--text-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;align-self:flex-start;">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
        </button>
      </div>
    `;

    return card;
  }

  private _renderCompactCard(item: ContentItem, locale: string, isRtl: boolean): HTMLElement {
    const card = document.createElement('div');
    card.className = 'watch-card';
    card.dataset.contentId = item.id;
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', item.title[locale] || item.title.en);
    card.style.cssText = `
      background:var(--card-bg);border-radius:16px;overflow:hidden;
      box-shadow:var(--shadow-sm);cursor:pointer;transition:transform 150ms;
    `;

    const title = item.title[locale] || item.title.en;
    const sourceColor = SOURCE_COLORS[item.source] || 'var(--accent)';
    const isLive = item.type === 'live_tv' || item.type === 'live_radio';

    card.innerHTML = `
      <div style="position:relative;width:100%;aspect-ratio:16/9;background:var(--bg-secondary);">
        ${item.thumbnail
          ? `<img src="${item.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;" loading="lazy"/>`
          : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${sourceColor}40,${sourceColor}20);display:flex;align-items:center;justify-content:center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${sourceColor}" stroke-width="2"><polygon points="5,3 19,12 5,21"/></svg>
            </div>`
        }
        ${item.duration ? `<span style="position:absolute;bottom:4px;${isRtl ? 'left' : 'right'}:4px;background:rgba(0,0,0,0.75);color:white;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:600;">${item.duration}m</span>` : ''}
        ${isLive ? `<span style="position:absolute;top:4px;${isRtl ? 'right' : 'left'}:4px;background:#EF4444;color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;">LIVE</span>` : ''}
      </div>
      <div style="padding:10px;">
        <p class="text-xs font-bold line-clamp-2" style="color:var(--text-primary);margin-bottom:4px;">${this._escapeHtml(title)}</p>
        <span class="text-xs" style="color:${sourceColor};">${item.source}</span>
      </div>
    `;

    return card;
  }

  // ---------- Saved Content View ----------

  private renderSavedView(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const locale = this.ctx.i18n.locale;

    this.container.innerHTML = '';

    const root = document.createElement('div');
    root.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    root.style.cssText = 'padding:8px 16px 100px;';

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'flex items-center gap-2 mb-4 text-sm font-medium';
    backBtn.style.cssText = 'color:var(--accent);background:none;border:none;cursor:pointer;padding:8px 0;';
    backBtn.innerHTML = `
      <svg class="w-4 h-4 ${isRtl ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      ${t('watch_back_to_browse')}
    `;
    backBtn.addEventListener('click', () => {
      this.showingSaved = null;
      this.renderBrowse();
    });
    root.appendChild(backBtn);

    // Tabs: Watch Later / Favorites
    const tabRow = document.createElement('div');
    tabRow.style.cssText = 'display:flex;gap:8px;margin-bottom:16px;';
    (['later', 'favorites'] as const).forEach((tab) => {
      const isActive = this.showingSaved === tab;
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding:8px 16px;border-radius:12px;border:none;cursor:pointer;font-size:13px;font-weight:600;
        ${isActive ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--card-bg);color:var(--text-secondary);border:1px solid var(--border);'}
      `;
      btn.textContent = tab === 'later' ? t('watch_watch_later') : t('watch_favorites');
      btn.addEventListener('click', () => {
        this.showingSaved = tab;
        this.renderSavedView();
      });
      tabRow.appendChild(btn);
    });
    root.appendChild(tabRow);

    // Items
    const ids = this.showingSaved === 'later' ? this._getWatchLater() : this._getFavorites();
    const items = ids.map((id) => CONTENT_CATALOG.find((c) => c.id === id)).filter(Boolean) as ContentItem[];

    if (items.length === 0) {
      root.innerHTML += `<p class="text-sm text-center py-12" style="color:var(--text-secondary);">${t('watch_no_saved')}</p>`;
    } else {
      const grid = document.createElement('div');
      grid.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
      items.forEach((item) => {
        const card = this._renderFullCard(item, locale, isRtl);
        grid.appendChild(card);
      });
      root.appendChild(grid);

      // Bind card clicks
      root.querySelectorAll<HTMLElement>('.watch-card').forEach((card) => {
        card.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('.watch-card-menu')) return;
          const id = card.dataset.contentId;
          const item = CONTENT_CATALOG.find((c) => c.id === id);
          if (item) this._openContent(item);
        });
      });
    }

    this.container.appendChild(root);
  }

  // ---------- Filter Panel ----------

  private _renderFilterPanel(t: (key: string) => string, locale: string): HTMLElement {
    const panel = document.createElement('div');
    panel.style.cssText = 'padding:8px 16px;';

    const wrap = document.createElement('div');
    wrap.style.cssText = 'background:var(--card-bg);border-radius:16px;padding:16px;box-shadow:var(--shadow-sm);';

    // Language filter
    const languages = ['en', 'fr', 'ar', 'es', 'de'];
    wrap.innerHTML += `
      <p class="text-xs font-bold uppercase tracking-wide mb-2" style="color:var(--text-secondary);">${t('watch_filter_language')}</p>
      <div class="watch-filter-lang flex gap-2 mb-3 flex-wrap">
        <button class="watch-flang px-3 py-1.5 rounded-full text-xs font-medium" data-lang="" style="${!this.activeFilters.language ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">${t('blog_all_categories')}</button>
        ${languages.map((l) => `
          <button class="watch-flang px-3 py-1.5 rounded-full text-xs font-medium" data-lang="${l}" style="${this.activeFilters.language === l ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">${LANG_FLAGS[l]} ${l.toUpperCase()}</button>
        `).join('')}
      </div>
    `;

    // Duration filter
    wrap.innerHTML += `
      <p class="text-xs font-bold uppercase tracking-wide mb-2" style="color:var(--text-secondary);">${t('watch_filter_duration')}</p>
      <div class="watch-filter-dur flex gap-2 mb-3 flex-wrap">
        <button class="watch-fdur px-3 py-1.5 rounded-full text-xs font-medium" data-dur="" style="${!this.activeFilters.duration ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">${t('blog_all_categories')}</button>
        <button class="watch-fdur px-3 py-1.5 rounded-full text-xs font-medium" data-dur="short" style="${this.activeFilters.duration === 'short' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">&lt;10 min</button>
        <button class="watch-fdur px-3 py-1.5 rounded-full text-xs font-medium" data-dur="medium" style="${this.activeFilters.duration === 'medium' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">10-30 min</button>
        <button class="watch-fdur px-3 py-1.5 rounded-full text-xs font-medium" data-dur="long" style="${this.activeFilters.duration === 'long' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">30+ min</button>
      </div>
    `;

    // Age range filter
    wrap.innerHTML += `
      <p class="text-xs font-bold uppercase tracking-wide mb-2" style="color:var(--text-secondary);">${t('watch_filter_age')}</p>
      <div class="watch-filter-age flex gap-2 flex-wrap">
        <button class="watch-fage px-3 py-1.5 rounded-full text-xs font-medium" data-age="" style="${!this.activeFilters.ageRange ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">${t('blog_all_categories')}</button>
        <button class="watch-fage px-3 py-1.5 rounded-full text-xs font-medium" data-age="kids" style="${this.activeFilters.ageRange === 'kids' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">Kids</button>
        <button class="watch-fage px-3 py-1.5 rounded-full text-xs font-medium" data-age="teens" style="${this.activeFilters.ageRange === 'teens' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">Teens</button>
        <button class="watch-fage px-3 py-1.5 rounded-full text-xs font-medium" data-age="all" style="${this.activeFilters.ageRange === 'all' ? 'background:var(--accent);color:var(--accent-text);' : 'background:var(--bg-secondary);color:var(--text-secondary);'}border:none;cursor:pointer;">${t('blog_all_categories')}</button>
      </div>
    `;

    panel.appendChild(wrap);
    return panel;
  }

  // ---------- Event Binding ----------

  private _bindBrowseEvents(root: HTMLElement): void {
    // Search
    const searchInput = root.querySelector('.watch-search') as HTMLInputElement;
    searchInput?.addEventListener('input', () => {
      this.searchQuery = searchInput.value;
      this._refreshContent();
    });

    // Filter toggle
    root.querySelector('.watch-filter-btn')?.addEventListener('click', () => {
      this.showFilters = !this.showFilters;
      this.renderBrowse();
    });

    // Saved button
    root.querySelector('.watch-saved-btn')?.addEventListener('click', () => {
      this.showingSaved = 'later';
      this.renderSavedView();
    });

    // Tab clicks
    root.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeTab = btn.dataset.tab as WatchTab;
        this.searchQuery = '';
        this.activeFilters = {};
        this.showFilters = false;
        this.renderBrowse();
      });
    });

    // Filter buttons
    root.querySelectorAll<HTMLButtonElement>('.watch-flang').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeFilters.language = btn.dataset.lang || undefined;
        this.renderBrowse();
      });
    });
    root.querySelectorAll<HTMLButtonElement>('.watch-fdur').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeFilters.duration = (btn.dataset.dur || undefined) as 'short' | 'medium' | 'long' | undefined;
        this.renderBrowse();
      });
    });
    root.querySelectorAll<HTMLButtonElement>('.watch-fage').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeFilters.ageRange = btn.dataset.age || undefined;
        this.renderBrowse();
      });
    });

    // Content card clicks
    root.querySelectorAll<HTMLElement>('.watch-card').forEach((card) => {
      const handler = (e: Event) => {
        if ((e.target as HTMLElement).closest('.watch-card-menu')) return;
        const id = card.dataset.contentId;
        const item = CONTENT_CATALOG.find((c) => c.id === id);
        if (item) this._openContent(item);
      };
      card.addEventListener('click', handler);
      card.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(e); }
      });
    });

    // Featured card click
    root.querySelector('.watch-featured')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.watch-card-menu')) return;
      const id = (e.currentTarget as HTMLElement).dataset.contentId;
      const item = CONTENT_CATALOG.find((c) => c.id === id);
      if (item) this._openContent(item);
    });

    // Card menus
    root.querySelectorAll<HTMLButtonElement>('.watch-card-menu').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCardMenu(btn, btn.dataset.id || '');
      });
    });

    // Active state animation on cards
    root.querySelectorAll<HTMLElement>('.watch-card').forEach((card) => {
      card.addEventListener('pointerdown', () => { card.style.transform = 'scale(0.98)'; });
      card.addEventListener('pointerup', () => { card.style.transform = ''; });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  // ---------- Card Context Menu ----------

  private _showCardMenu(anchor: HTMLElement, itemId: string): void {
    if (!this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';

    // Remove existing menus
    document.querySelectorAll('.watch-card-popup').forEach((el) => el.remove());

    const isInWatchLater = this._getWatchLater().includes(itemId);
    const isInFavorites = this._getFavorites().includes(itemId);

    const menu = document.createElement('div');
    menu.className = 'watch-card-popup';
    menu.style.cssText = `
      position:fixed;z-index:100;background:var(--card-bg);border:1px solid var(--border);
      border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.15);min-width:180px;overflow:hidden;
      animation:widgetMenuIn 150ms ease;
    `;

    const actions = [
      {
        label: isInWatchLater ? t('watch_remove_later') : t('watch_save_later'),
        icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="${isInWatchLater ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>`,
        action: () => { this._toggleWatchLater(itemId); },
      },
      {
        label: isInFavorites ? t('watch_remove_favorite') : t('watch_add_favorite'),
        icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="${isInFavorites ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`,
        action: () => { this._toggleFavorite(itemId); },
      },
      {
        label: t('watch_hide'),
        icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
        action: () => { this._hideContent(itemId); this.renderBrowse(); },
      },
      {
        label: t('watch_share'),
        icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`,
        action: () => {
          const item = CONTENT_CATALOG.find((c) => c.id === itemId);
          if (item) {
            // [DECISION] Copy link to clipboard instead of using navigator.share
            // which could open external apps and break the walled garden.
            navigator.clipboard?.writeText(item.url).catch(() => {});
          }
        },
      },
    ];

    actions.forEach((act) => {
      const btn = document.createElement('button');
      btn.style.cssText = `
        display:flex;align-items:center;gap:10px;width:100%;padding:12px 16px;border:none;
        background:transparent;cursor:pointer;font-size:13px;color:var(--text-primary);
        text-align:${isRtl ? 'right' : 'left'};transition:background 150ms;
      `;
      btn.innerHTML = `${act.icon}<span>${act.label}</span>`;
      btn.addEventListener('mouseenter', () => { btn.style.background = 'var(--bg-secondary)'; });
      btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        act.action();
        menu.remove();
      });
      menu.appendChild(btn);
    });

    // Position near anchor
    const rect = anchor.getBoundingClientRect();
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style[isRtl ? 'left' : 'right'] = `${window.innerWidth - rect.right}px`;

    document.body.appendChild(menu);

    // Close on outside click
    const closeHandler = () => {
      menu.remove();
      document.removeEventListener('click', closeHandler);
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  // ---------- Open Content ----------

  private _openContent(item: ContentItem): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const isRtl = this.ctx.i18n.dir === 'rtl';

    // [DECISION] Parental control check before playback
    if (this._isChildProfile()) {
      if (item.age_range === 'adults' || item.age_range === 'teens') {
        // Block access for child profiles
        this.ctx.showNotification(t('watch_restricted'), 'warning');
        return;
      }
    }

    if (!this._canAccessWatchCategory(item.category)) {
      this.ctx.showNotification(t('watch_restricted'), 'warning');
      return;
    }

    this.viewingContent = item;
    this._saveContinueWatching(item.id);

    // Podcast: use audio player
    if (item.type === 'podcast') {
      this.container.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.style.cssText = 'padding:16px;max-width:480px;margin:0 auto;';
      wrap.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

      // Back button
      const backBtn = document.createElement('button');
      backBtn.className = 'flex items-center gap-2 mb-6 text-sm font-medium';
      backBtn.style.cssText = 'color:var(--accent);background:none;border:none;cursor:pointer;padding:8px 0;';
      backBtn.innerHTML = `
        <svg class="w-4 h-4 ${isRtl ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        ${t('watch_back_to_browse')}
      `;
      backBtn.addEventListener('click', () => {
        this.viewingContent = null;
        this.renderBrowse();
        // Show mini bar if audio still playing
        if (this.audioPlayer.isActive() && this.container) {
          this.audioPlayer.renderMiniBar(document.body, t);
        }
      });
      wrap.appendChild(backBtn);

      const playerContainer = document.createElement('div');
      wrap.appendChild(playerContainer);

      // Description
      const desc = document.createElement('div');
      desc.style.cssText = 'margin-top:20px;';
      desc.innerHTML = `
        <p class="text-sm leading-relaxed" style="color:var(--text-secondary);">${this._escapeHtml(item.description[locale] || item.description.en)}</p>
        ${item.url && !item.url.startsWith('https://www.youtube') ? `
          <button class="watch-copy-source-btn inline-flex items-center gap-2 mt-4 text-sm font-medium" style="color:var(--accent);background:none;border:none;cursor:pointer;">
            ${t('watch_copy_link')}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
        ` : ''}
      `;

      // Bind copy-link button (no external navigation)
      const copyBtn = desc.querySelector('.watch-copy-source-btn');
      if (copyBtn) {
        copyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          navigator.clipboard?.writeText(item.url).then(() => {
            (copyBtn as HTMLElement).innerHTML = `<span>\u2713 ${t('watch_copy_link')}</span>`;
            setTimeout(() => {
              (copyBtn as HTMLElement).innerHTML = `
                ${t('watch_copy_link')}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              `;
            }, 2000);
          }).catch(() => {});
        });
      }

      wrap.appendChild(desc);

      this.container.appendChild(wrap);

      // Load and render audio player
      this.audioPlayer.load(item.url, item.title[locale] || item.title.en, item.source);
      this.audioPlayer.render(playerContainer, t, () => {
        this.viewingContent = null;
        this.renderBrowse();
      });
      return;
    }

    // Video / Live / External: use VideoEmbed
    this.videoEmbed.render(this.container, item, locale, t, isRtl, () => {
      this.viewingContent = null;
      this.renderBrowse();
    });
  }

  // ---------- Content Data Helpers ----------

  private _getTabContent(locale: string): ContentItem[] {
    const hidden = this._getHidden();
    let items = getContentByTab(this.activeTab, locale).filter((i) => !hidden.includes(i.id));

    // [DECISION] Parental controls: filter content based on profile restrictions
    const isChildProfile = this._isChildProfile();
    if (isChildProfile) {
      // Children only see kids-appropriate content
      items = items.filter((i) => i.age_range === 'kids' || i.age_range === 'all');
    }

    // Check if specific watch categories are blocked via parental controls
    items = items.filter((i) => this._canAccessWatchCategory(i.category));

    if (this.searchQuery) {
      items = searchContent(this.searchQuery, items, locale);
    }

    if (Object.keys(this.activeFilters).length > 0) {
      items = filterContent(items, this.activeFilters);
    }

    return items;
  }

  private _refreshContent(): void {
    if (!this.container || !this.ctx) return;
    const contentArea = this.container.querySelector('.watch-content');
    if (!contentArea) return;

    const locale = this.ctx.i18n.locale;
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const items = this._getTabContent(locale);

    contentArea.innerHTML = '';
    if (this.activeTab === 'for_you' && !this.searchQuery && Object.keys(this.activeFilters).length === 0) {
      this._renderForYouTab(contentArea as HTMLElement, items, t, locale, isRtl);
    } else {
      this._renderContentGrid(contentArea as HTMLElement, items, t, locale, isRtl);
    }

    // Re-bind card events
    contentArea.querySelectorAll<HTMLElement>('.watch-card').forEach((card) => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.watch-card-menu')) return;
        const id = card.dataset.contentId;
        const item = CONTENT_CATALOG.find((c) => c.id === id);
        if (item) this._openContent(item);
      });
      card.addEventListener('pointerdown', () => { card.style.transform = 'scale(0.98)'; });
      card.addEventListener('pointerup', () => { card.style.transform = ''; });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });

    contentArea.querySelectorAll<HTMLButtonElement>('.watch-card-menu').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCardMenu(btn, btn.dataset.id || '');
      });
    });

    // Featured click
    contentArea.querySelector('.watch-featured')?.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.contentId;
      const item = CONTENT_CATALOG.find((c) => c.id === id);
      if (item) this._openContent(item);
    });
  }

  // ---------- Tab Visibility ----------

  private _getVisibleTabs(): TabDef[] {
    // [DECISION] Filter tabs based on parental controls and child profile
    const isChild = this._isChildProfile();
    return TABS.filter((tab) => {
      // If child profile, only show Kids and For You tabs
      if (isChild && tab.id !== 'kids' && tab.id !== 'for_you') return false;
      // Check if category blocked via parental controls
      if (tab.id === 'islamic' && !this._canAccessWatchCategory('islamic')) return false;
      return true;
    });
  }

  // ---------- Featured Rotation ----------

  private _startFeaturedRotation(): void {
    this._stopFeaturedRotation();
    this.featuredInterval = setInterval(() => {
      this.featuredIndex++;
      if (this.container && this.activeTab === 'for_you' && !this.viewingContent && !this.showingSaved) {
        const featured = getFeaturedContent();
        const current = featured[this.featuredIndex % featured.length];
        const el = this.container.querySelector('.watch-featured') as HTMLElement;
        if (el && current) {
          el.style.transition = 'opacity 300ms';
          el.style.opacity = '0';
          setTimeout(() => {
            el.dataset.contentId = current.id;
            const locale = this.ctx?.i18n.locale || 'en';
            const img = el.querySelector('img');
            if (img && current.thumbnail) img.src = current.thumbnail;
            const title = el.querySelector('h2');
            if (title) title.textContent = current.title[locale] || current.title.en;
            const desc = el.querySelector('p:last-of-type');
            if (desc) desc.textContent = current.description[locale] || current.description.en;
            const badge = el.querySelector('.px-2');
            if (badge) {
              badge.textContent = current.source;
              (badge as HTMLElement).style.background = SOURCE_COLORS[current.source] || 'var(--accent)';
            }
            el.style.opacity = '1';
          }, 300);
        }
      }
    }, 8000);
  }

  private _stopFeaturedRotation(): void {
    if (this.featuredInterval) {
      clearInterval(this.featuredInterval);
      this.featuredInterval = null;
    }
  }

  // ---------- localStorage Helpers ----------

  private _getWatchLater(): string[] {
    try { return JSON.parse(localStorage.getItem(LS_WATCH_LATER) || '[]'); } catch { return []; }
  }

  private _getFavorites(): string[] {
    try { return JSON.parse(localStorage.getItem(LS_FAVORITES) || '[]'); } catch { return []; }
  }

  private _getContinueWatching(): ContentItem[] {
    try {
      const ids: string[] = JSON.parse(localStorage.getItem(LS_CONTINUE) || '[]');
      return ids.map((id) => CONTENT_CATALOG.find((c) => c.id === id)).filter(Boolean).slice(0, 5) as ContentItem[];
    } catch { return []; }
  }

  private _getHidden(): string[] {
    try { return JSON.parse(localStorage.getItem(LS_HIDDEN) || '[]'); } catch { return []; }
  }

  private _toggleWatchLater(id: string): void {
    const list = this._getWatchLater();
    const idx = list.indexOf(id);
    if (idx >= 0) list.splice(idx, 1); else list.unshift(id);
    try { localStorage.setItem(LS_WATCH_LATER, JSON.stringify(list)); } catch { /* */ }
  }

  private _toggleFavorite(id: string): void {
    const list = this._getFavorites();
    const idx = list.indexOf(id);
    if (idx >= 0) list.splice(idx, 1); else list.unshift(id);
    try { localStorage.setItem(LS_FAVORITES, JSON.stringify(list)); } catch { /* */ }
  }

  private _saveContinueWatching(id: string): void {
    try {
      const list: string[] = JSON.parse(localStorage.getItem(LS_CONTINUE) || '[]');
      const idx = list.indexOf(id);
      if (idx >= 0) list.splice(idx, 1);
      list.unshift(id);
      localStorage.setItem(LS_CONTINUE, JSON.stringify(list.slice(0, 10)));
    } catch { /* */ }
  }

  private _hideContent(id: string): void {
    try {
      const list: string[] = JSON.parse(localStorage.getItem(LS_HIDDEN) || '[]');
      if (!list.includes(id)) list.push(id);
      localStorage.setItem(LS_HIDDEN, JSON.stringify(list));
    } catch { /* */ }
  }

  // ---------- Link Interception (SAFETY) ----------

  /**
   * Intercept ALL anchor clicks inside the watch module.
   * NEVER open external links -- users must stay in the app.
   * This is a SAFETY requirement for child profiles.
   */
  private _interceptExternalLinks(container: HTMLElement): void {
    container.addEventListener('click', (e) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor && anchor.href) {
        e.preventDefault();
        e.stopPropagation();
        // Copy link to clipboard instead of opening it
        navigator.clipboard?.writeText(anchor.href).catch(() => {});
      }
    });
  }

  // ---------- Parental Controls ----------

  /**
   * Check if the active profile is a child profile.
   * Uses plan or context hints to determine profile type.
   */
  private _isChildProfile(): boolean {
    if (!this.ctx) return false;
    const user = this.ctx.auth.user;
    if (!user) return false;
    // [DECISION] Use plan-based heuristic: child profiles typically have specific markers.
    // The ParentalControlService.canAccessModule checks are more reliable.
    const profile = user as unknown as Record<string, unknown>;
    return profile.type === 'child' || profile.profile_type === 'child';
  }

  /**
   * Check if a specific watch category is accessible given parental controls.
   * Blocked modules/categories are managed via ParentalControlService.
   */
  private _canAccessWatchCategory(category: string): boolean {
    try {
      const parental = getParentalControlService();
      if (!parental || !parental.isEnabled) return true;

      // Check if 'watch' module itself is blocked
      if (!parental.canAccessModule('watch')) return false;

      // Check if specific watch categories are blocked (e.g., 'watch:islamic', 'watch:news')
      if (!parental.canAccessModule(`watch:${category}`)) return false;

      return true;
    } catch {
      // ParentalControlService not available -- allow all
      return true;
    }
  }

  // ---------- Utility ----------

  private _escapeHtml(text: string): string {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }

  private _injectStyles(): void {
    if (document.getElementById('watch-module-styles')) return;
    const style = document.createElement('style');
    style.id = 'watch-module-styles';
    style.textContent = `
      @keyframes livePulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }
      .watch-tabs::-webkit-scrollbar { display: none; }
      .watch-card:hover { transform: translateY(-2px); }
      .watch-card:active { transform: scale(0.98) !important; }
      .watch-featured:hover .watch-featured-play {
        transform: translate(-50%, -50%) scale(1.1);
      }
      .line-clamp-1 { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; }
      .line-clamp-2 { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    `;
    document.head.appendChild(style);
  }
}
